import { AfterViewInit, Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductService } from '../../../services/product.service';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { InputSwitchModule } from 'primeng/inputswitch';
import { TextareaModule } from 'primeng/textarea';
import { GroupService } from '../../groups/group.service';
import { BrandService } from '../../brands/brand.service';
import { MeasureUniteService } from '../../measure-of-unities/measure-unite.service';
import { SectorService } from '../../sectors/sector.service';
import { SubgroupService } from '../../subgroups/subgroup.service';
import { MeasureUnite } from '../../../models/measureUnite.model';
import { Group } from '../../../models/group.model';
import { Brand } from '../../../models/brand.model';
import { Sector } from '../../../models/sector.model';
import { Subgroup } from '../../../models/subgroup.model';
import { Product } from '../../../models/product.model';
import { ModalComponent } from '../../../components/modal/modal.component';
import { CreateMeasureOfUnitComponent } from '../../measure-of-unities/create-measure-of-unit/create-measure-of-unit.component';
import { CreateGroupComponent } from '../../groups/create-group/create-group.component';
import { CreateBrandComponent } from '../../brands/create-brand/create-brand.component';
import { CreateSubgroupComponent } from '../../subgroups/create-subgroup/create-subgroup.component';
import { CreateSectorComponent } from '../../sectors/create-sector/create-sector.component';
import { CreateFamilyComponent } from '../../families/create-family/create-family.component';
import { CommonModule } from '@angular/common';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { Family } from '../../../models/family.model';
import { FamilyService } from '../../families/family.service';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-form-product',
  imports: [
    CommonModule,
    InputSwitchModule,
    ButtonModule,
    ReactiveFormsModule,
    SelectModule,
    InputTextModule,
    TextareaModule,
    InputNumberModule,
    DatePickerModule,
    TabsModule,
    ModalComponent,
    CreateMeasureOfUnitComponent,
    CreateGroupComponent,
    CreateBrandComponent,
    CreateSubgroupComponent,
    CreateSectorComponent,
    CreateFamilyComponent,
    CheckboxModule,
    TooltipModule
  ],
  templateUrl: './form-product.component.html'
})
export class FormProductComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('productCodeInput', { read: ElementRef }) private productCodeInput?: ElementRef<HTMLInputElement>;

  form!: FormGroup;
  isEditMode = false;
  showUniteMeasureForm = false;
  showGroupForm = false;
  showBrandForm = false;
  showSubgroupForm = false;
  showSectorForm = false;
  showFamilyForm = false;
  id!: number;
  returnTo: string | null = null;

  brands!: Brand[];
  unitsMeasures!: MeasureUnite[];
  groups: Group[] = [];
  sectors: Sector[] = [];
  subgroups: Subgroup[] = [];
  families: Family[] = [];
  originalPrices: { priceSelling: number; priceTerm: number; priceWholeSale: number } | null = null;

  constructor(
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private productService: ProductService,
    private router: Router,
    private groupService: GroupService,
    private brandService: BrandService,
    private measureUniteService: MeasureUniteService,
    private sectorService: SectorService,
    private subgroupService: SubgroupService,
    private familyService: FamilyService
  ) {}

  ngOnInit(): void {
    this.returnTo = this.activatedRoute.snapshot.queryParamMap.get('returnTo');
    this.getAuxiliarData();

    this.form = this.fb.group({
      code: [''],
      active: [true],
      barCode: [''],
      description: ['', Validators.required],
      localization: [''],
      especification: [''],
      measureUnityId: [null],
      sectorId: [null as number | null],
      groupId: [null as number | null],
      subgroupId: [null as number | null],
      familyId: [null as number | null],
      brandId: [null],
      minimumStock: [0],
      currentStock: [0],
      cashBack: [0],
      discountLimit: [0],
      comission: [0],
      priceCost: [0],
      custosOperacionais: [0],
      priceSelling: [0, [Validators.required, Validators.min(1)]],
      priceTerm: [0],
      profitTerm: [0],
      profitWholeSale: [0],
      profitSelling: [0],
      priceWholeSale: [0],
      validAt: [],
      balanceCode: [''],
      weight: [0],
      width: [0],
      height: [0],
      length: [0],
      idStock: [null]
    });

    this.form
      .get('familyId')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((familyId) => {
        if (familyId == null || familyId === '') {
          return;
        }
        const id = typeof familyId === 'number' ? familyId : Number(familyId);
        if (!Number.isFinite(id)) {
          return;
        }
        this.fillPricesFromFamily(id);
      });

    const idParam = this.activatedRoute.snapshot.paramMap.get('id');
    if (idParam) {
      this.id = parseInt(idParam, 10);
      this.isEditMode = true;

      this.productService.getById(this.id).subscribe({
        next: res => {
          const p = res.data as Product;
          this.applyProductFromApi(p);
        },
        error: () => {}
      });
    } else {
      const duplicateFrom = this.activatedRoute.snapshot.queryParamMap.get('duplicateFrom');
      if (duplicateFrom != null && duplicateFrom !== '') {
        const sourceId = parseInt(duplicateFrom, 10);
        if (Number.isFinite(sourceId)) {
          this.productService.getById(sourceId).subscribe({
            next: res => {
              const p = res.data as Product;
              if (p) {
                this.applyProductFromApi(p);
                this.form.patchValue(
                  { barCode: null, code: '', balanceCode: '' },
                  { emitEvent: false }
                );
                void this.router.navigate([], {
                  relativeTo: this.activatedRoute,
                  queryParams: { duplicateFrom: null },
                  queryParamsHandling: 'merge',
                  replaceUrl: true
                });
              }
            },
            error: () => {}
          });
        }
      }
    }
  }

  duplicateCurrent(): void {
    if (!this.isEditMode) {
      return;
    }
    this.router.navigate(['/app/products/new'], { queryParams: { duplicateFrom: this.id } });
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.productCodeInput?.nativeElement?.focus());
  }

  private applyProductFromApi(p: Product): void {
    const sectorId = p.sectorId ?? p.group?.sectorId ?? null;
    this.originalPrices = {
      priceSelling: Number(p.priceSelling ?? 0),
      priceTerm: Number(p.priceTerm ?? 0),
      priceWholeSale: Number(p.priceWholeSale ?? 0)
    };
    this.form.patchValue(
      {
        ...p,
        sectorId
      },
      { emitEvent: false }
    );

    if (sectorId != null) {
      this.reloadGroups(sectorId, () => {
        if (p.groupId) {
          this.reloadSubgroups(p.groupId);
        }
      });
    } else {
      this.reloadGroups(undefined, () => {
        if (p.groupId) {
          this.reloadSubgroups(p.groupId);
        }
      });
    }
  }

  getAuxiliarData(): void {
    this.getSectors();
    this.reloadGroups(undefined);
    this.getMeasureUnities();
    this.getBrands();
    this.getFamilies();
  }

  getSectors(): void {
    this.sectorService.getAll('', false, null).subscribe({
      next: res => (this.sectors = res.data)
    });
  }

  reloadGroups(sectorId: number | null | undefined, after?: () => void): void {
    this.groupService.getAll('', null, false, null, sectorId ?? undefined).subscribe({
      next: res => {
        this.groups = res.data;
        after?.();
      }
    });
  }

  reloadSubgroups(groupId: number | null): void {
    if (!groupId) {
      this.subgroups = [];
      return;
    }
    this.subgroupService.getAll('', groupId, false, null).subscribe({
      next: res => (this.subgroups = res.data)
    });
  }

  onSectorChange(): void {
    const sectorId = this.form.get('sectorId')?.value as number | null;
    this.reloadGroups(sectorId ?? undefined);
    this.form.patchValue({ groupId: null, subgroupId: null }, { emitEvent: false });
    this.subgroups = [];
  }

  onGroupChange(): void {
    const gid = this.form.get('groupId')?.value as number | null;
    this.form.patchValue({ subgroupId: null }, { emitEvent: false });
    this.reloadSubgroups(gid);
  }

  getMeasureUnities(): void {
    this.measureUniteService.getAll('', true, false, null).subscribe({
      next: data => {
        this.unitsMeasures = data.data;
        this.form.get('measureUnityId')?.setValue(this.unitsMeasures[0].id);
      }
    });
  }

  getBrands(): void {
    this.brandService.getAll('', true, false, null).subscribe({
      next: data => {
        this.brands = data.data;
      }
    });
  }

  getFamilies(): void {
    this.familyService.getAll('', true, 1, false).subscribe({
      next: data => {
        this.families = data.data ?? [];
      }
    });
  }

  /** Preenche preços de venda/prazo/atacado a partir da família (lista ou API). */
  private fillPricesFromFamily(familyId: number): void {
    const fromList = this.families.find((f) => f.id === familyId);
    if (fromList) {
      this.applyFamilyPricesToForm(fromList);
      return;
    }
    this.familyService.getById(familyId).subscribe({
      next: (res) => {
        if (res.data) {
          this.applyFamilyPricesToForm(res.data);
        }
      },
      error: () => {}
    });
  }

  private applyFamilyPricesToForm(family: Family): void {
    const priceSelling = Number(family.priceSelling ?? 0);
    const priceTerm = Number(family.priceTerm ?? 0);
    const priceWholeSale = Number(family.priceWholeSale ?? 0);
    this.form.patchValue({ priceSelling, priceTerm, priceWholeSale }, { emitEvent: false });
    this.calculateProfit('priceSelling');
    this.calculateProfit('priceTerm');
    this.calculateProfit('priceWholeSale');
  }

  goBack(): void {
    if (this.returnTo) {
      this.router.navigateByUrl(this.returnTo);
    } else {
      this.router.navigate(['/app/products']);
    }
  }

  /** Enter no campo vazio gera código (igual ao cadastro legado). */
  onBarCodeEnter(event: Event): void {
    const input = event.target as HTMLInputElement;
    if ((input.value ?? '').length > 0) {
      return;
    }
    event.preventDefault();
    this.generateRandomBarCode();
  }

  /** Mesma lógica do cadastro legado (EAN-13 interno: prefixo 7 + aleatório + DV). */
  generateRandomBarCode(): void {
    const val1 = Math.floor(100000 + Math.random() * 999999);
    const val2 = Math.floor(10000 + Math.random() * 99999);
    const body = ('7' + val1 + '' + val2).slice(0, 12);
    const full = body + this.calculateEan13CheckDigit(body);
    this.form.get('barCode')?.setValue(full);
    this.form.get('barCode')?.markAsDirty();
  }

  private calculateEan13CheckDigit(twelveDigits: string): number {
    let factor = 3;
    let sum = 0;
    for (let index = twelveDigits.length; index > 0; index--) {
      sum += Number(twelveDigits.substring(index - 1, index)) * factor;
      factor = 4 - factor;
    }
    return (1000 - sum) % 10;
  }

  private buildProductPayload(): Record<string, unknown> {
    const raw = { ...this.form.getRawValue() } as Record<string, unknown>;
    const barCode = raw['barCode'];
    raw['barCode'] = barCode === '' || barCode === undefined ? null : barCode;
    if (!this.isEditMode) {
      const code = raw['code'];
      raw['code'] = code === '' || code === undefined || code === null ? null : code;
      const balanceCode = raw['balanceCode'];
      raw['balanceCode'] =
        balanceCode === '' || balanceCode === undefined || balanceCode === null ? null : balanceCode;
    }
    return raw;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      return;
    }

    const payload = this.buildProductPayload();

    if (this.isEditMode) {
      const shouldReplicateToFamily = this.shouldReplicateToFamily();
      this.productService.update(this.id, payload, shouldReplicateToFamily).subscribe({
        next: () => this.goBack(),
        error: () => {}
      });
    } else {
      this.productService.create(payload).subscribe({
        next: () => this.goBack(),
        error: () => {}
      });
    }
  }

  changeModal(modal: string): void {
    switch (modal) {
      case 'uniteMeasure':
        this.showUniteMeasureForm = !this.showUniteMeasureForm;
        break;
      case 'group':
        this.showGroupForm = !this.showGroupForm;
        break;
      case 'sector':
        this.showSectorForm = !this.showSectorForm;
        break;
      case 'brand':
        this.showBrandForm = !this.showBrandForm;
        break;
      case 'family':
        this.showFamilyForm = !this.showFamilyForm;
        break;
      case 'subgroup':
        this.showSubgroupForm = !this.showSubgroupForm;
        break;
    }
  }

  onModalSubmit(modal: string): void {
    switch (modal) {
      case 'uniteMeasure':
        this.getMeasureUnities();
        break;
      case 'group': {
        const sid = this.form.get('sectorId')?.value as number | null | undefined;
        this.reloadGroups(sid ?? undefined);
        break;
      }
      case 'sector': {
        this.getSectors();
        const sid = this.form.get('sectorId')?.value as number | null | undefined;
        this.reloadGroups(sid ?? undefined);
        break;
      }
      case 'brand':
        this.getBrands();
        break;
      case 'family':
        this.getFamilies();
        break;
      case 'subgroup': {
        const gid = this.form.get('groupId')?.value as number | null;
        this.reloadSubgroups(gid);
        break;
      }
    }

    this.changeModal(modal);
  }

  calculateProfit(inputName: string): void {
    const parseNumber = (value: unknown): number => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const priceCost = parseNumber(this.form.get('priceCost')?.value);

    const updatePriceFromProfit = (profitControlName: string, priceControlName: string): void => {
      const profitPercent = parseNumber(this.form.get(profitControlName)?.value);
      const price = priceCost + priceCost * (profitPercent / 100);
      this.form.patchValue({ [priceControlName]: price });
    };

    const updateProfitFromPrice = (priceControlName: string, profitControlName: string): void => {
      const price = parseNumber(this.form.get(priceControlName)?.value);
      if (priceCost <= 0 || price <= 0) {
        return;
      }

      const profit = price - priceCost;
      const markup = (profit / priceCost) * 100;
      this.form.patchValue({ [profitControlName]: markup });
    };

    if (inputName === 'priceCost') {
      if (priceCost <= 0) {
        return;
      }
      const syncChannel = (priceControlName: string, profitControlName: string): void => {
        const price = parseNumber(this.form.get(priceControlName)?.value);
        if (price > 0) {
          updateProfitFromPrice(priceControlName, profitControlName);
        } else {
          updatePriceFromProfit(profitControlName, priceControlName);
        }
      };
      syncChannel('priceSelling', 'profitSelling');
      syncChannel('priceTerm', 'profitTerm');
      syncChannel('priceWholeSale', 'profitWholeSale');
      return;
    }

    if (inputName === 'profitSelling') {
      updatePriceFromProfit('profitSelling', 'priceSelling');
      return;
    }

    if (inputName === 'priceSelling') {
      updateProfitFromPrice('priceSelling', 'profitSelling');
      return;
    }

    if (inputName === 'profitTerm') {
      updatePriceFromProfit('profitTerm', 'priceTerm');
      return;
    }

    if (inputName === 'priceTerm') {
      updateProfitFromPrice('priceTerm', 'profitTerm');
      return;
    }

    if (inputName === 'profitWholeSale') {
      updatePriceFromProfit('profitWholeSale', 'priceWholeSale');
      return;
    }

    if (inputName === 'priceWholeSale') {
      updateProfitFromPrice('priceWholeSale', 'profitWholeSale');
    }
  }

  selectInputContent(event: { originalEvent?: { target?: unknown }; target?: unknown }): void {
    const inputElement = event?.originalEvent?.target ?? event?.target;

    if (!(inputElement instanceof HTMLInputElement)) {
      return;
    }

    setTimeout(() => inputElement.select(), 0);
  }

  private shouldReplicateToFamily(): boolean {
    if (!this.isEditMode || !this.originalPrices) {
      return false;
    }

    const familyId = this.form.get('familyId')?.value as number | null;
    if (!familyId) {
      return false;
    }

    const currentPriceSelling = Number(this.form.get('priceSelling')?.value ?? 0);
    const currentPriceTerm = Number(this.form.get('priceTerm')?.value ?? 0);
    const currentPriceWholeSale = Number(this.form.get('priceWholeSale')?.value ?? 0);

    const changed =
      currentPriceSelling !== this.originalPrices.priceSelling ||
      currentPriceTerm !== this.originalPrices.priceTerm ||
      currentPriceWholeSale !== this.originalPrices.priceWholeSale;

    if (!changed) {
      return false;
    }

    return confirm('Deseja replicar a alteração de preços para todos os produtos da mesma família?');
  }
}
